import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { validateTokenSize } from "../../utils/storageUtils";

interface User {
  id: string;
  login: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  provider?: {
    id: string;
    name: string;
    status: string;
  };
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Безопасные функции для работы с localStorage
const getStorageItem = (key: string): string | null => {
  try {
    if (typeof Storage !== "undefined" && typeof localStorage !== "undefined") {
      return localStorage.getItem(key);
    }
  } catch {
    // localStorage недоступен
  }
  return null;
};

const setStorageItem = (key: string, value: string): void => {
  try {
    if (typeof Storage !== "undefined" && typeof localStorage !== "undefined") {
      localStorage.setItem(key, value);
    }
  } catch {
    // localStorage недоступен
  }
};

const removeStorageItem = (key: string): void => {
  try {
    if (typeof Storage !== "undefined" && typeof localStorage !== "undefined") {
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage недоступен
  }
};

const initialToken = getStorageItem("token");
const initialRefreshToken = getStorageItem("refreshToken");
const initialUser = (() => {
  try {
    const userData = getStorageItem("user");
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
})();

const initialState: AuthState = {
  user: initialUser,
  token: initialToken,
  refreshToken: initialRefreshToken,
  isAuthenticated: !!initialToken,
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        refreshToken: string;
      }>
    ) => {
      const { token, refreshToken, user } = action.payload;

      // Критическая проверка роли - только PROVIDER может входить в provider panel
      if (user.role !== "PROVIDER") {
        state.error = `Доступ запрещен. Эта панель предназначена только для провайдеров. Ваша роль: ${user.role}`;
        state.isAuthenticated = false;
        removeStorageItem("token");
        removeStorageItem("refreshToken");
        return;
      }

      if (!validateTokenSize(token)) {
        state.error = "Token слишком большой";
        return;
      }

      if (!validateTokenSize(refreshToken)) {
        state.error = "Refresh token слишком большой";
        return;
      }

      state.isLoading = false;
      state.user = user;
      state.token = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.error = null;

      setStorageItem("token", token);
      setStorageItem("refreshToken", refreshToken);
      setStorageItem("user", JSON.stringify(user));
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;

      removeStorageItem("token");
      removeStorageItem("refreshToken");
      removeStorageItem("user");
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;

      removeStorageItem("token");
      removeStorageItem("refreshToken");
      removeStorageItem("user");
    },
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      setStorageItem("user", JSON.stringify(action.payload));
    },
    updateTokens: (
      state,
      action: PayloadAction<{ token: string; refreshToken: string }>
    ) => {
      const { token, refreshToken } = action.payload;

      if (!validateTokenSize(token) || !validateTokenSize(refreshToken)) {
        state.error = "Токены слишком большие";
        state.isAuthenticated = false;
        removeStorageItem("token");
        removeStorageItem("refreshToken");
        return;
      }

      state.token = token;
      state.refreshToken = refreshToken;

      setStorageItem("token", token);
      setStorageItem("refreshToken", refreshToken);
    },
    initializeAuth: (state) => {
      const token = getStorageItem("token");
      const refreshToken = getStorageItem("refreshToken");

      if (token && refreshToken) {
        state.token = token;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        state.isLoading = false;
      } else {
        state.isAuthenticated = false;
        state.isLoading = false;
      }
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  setUser,
  updateTokens,
  initializeAuth,
} = authSlice.actions;

export default authSlice.reducer;
